import React, { useState } from 'react';
import { Mail, MapPin, Clock, Send, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { useToast } from '../hooks/use-toast';
import { companyInfo } from '../mockData';
import { validateEmail, validatePhone, countryCodes, getEmailError, getPhoneError, formatPhoneNumber } from '../utils/validation';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleBlur = (field) => {
    setTouched({
      ...touched,
      [field]: true
    });

    // Validate on blur
    let error = '';
    if (field === 'email') {
      error = getEmailError(formData.email);
    }

    if (error) {
      setErrors({
        ...errors,
        [field]: error
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    const emailError = getEmailError(formData.email);
    if (emailError) {
      newErrors.email = emailError;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    setTouched({
      name: true,
      email: true,
      message: true
    });

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form before submitting.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast({
        title: "Message Sent Successfully!",
        description: "Thank you for contacting us. We'll get back to you within 24 hours.",
      });
      setFormData({
        name: '',
        email: '',
        message: ''
      });
      setErrors({});
      setTouched({});
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-background to-background -z-10" />
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
            Get in <span className="text-primary">Touch</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Have a question or ready to start a project? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-background lg:block hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
            <Card className="hover:shadow-lg shadow-primary/50 transition-shadow duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-primary" size={24} />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Email Us</h3>
                <a href={`mailto:${companyInfo.email}`} className="text-primary hover:text-primary text-sm">
                  {companyInfo.email}
                </a>
              </CardContent>
            </Card>

            {/* <Card className="hover:shadow-lg shadow-primary/50 transition-shadow duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-primary" size={24} />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Visit Us</h3>
                <p className="text-muted-foreground text-sm">
                  San Francisco, CA 94108
                </p>
              </CardContent>
            </Card> */}

            <Card className="hover:shadow-lg shadow-primary/50 transition-shadow duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="text-primary" size={24} />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Business Hours</h3>
                <p className="text-muted-foreground text-sm">
                  Mon - Fri: 9AM - 6PM EST
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Info Cards - Mobile/Tablet Slider */}
      <section className="py-12 bg-background lg:hidden block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 3000,
              }),
            ]}
            className="w-full"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              <CarouselItem className="pl-2 md:pl-4 basis-4/5 sm:basis-1/2">
                <Card className="hover:shadow-lg shadow-primary/50 transition-shadow duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="text-primary" size={24} />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Email Us</h3>
                    <a href={`mailto:${companyInfo.email}`} className="text-primary hover:text-primary text-sm">
                      {companyInfo.email}
                    </a>
                  </CardContent>
                </Card>
              </CarouselItem>

              <CarouselItem className="pl-2 md:pl-4 basis-4/5 sm:basis-1/2">
                <Card className="hover:shadow-lg shadow-primary/50 transition-shadow duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MapPin className="text-primary" size={24} />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Visit Us</h3>
                    <p className="text-muted-foreground text-sm">
                      San Francisco, CA 94108
                    </p>
                  </CardContent>
                </Card>
              </CarouselItem>

              <CarouselItem className="pl-2 md:pl-4 basis-4/5 sm:basis-1/2">
                <Card className="hover:shadow-lg shadow-primary/50 transition-shadow duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="text-primary" size={24} />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Business Hours</h3>
                    <p className="text-muted-foreground text-sm">
                      Mon - Fri: 9AM - 6PM EST
                    </p>
                  </CardContent>
                </Card>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex" />
            <CarouselNext className="hidden sm:flex" />
          </Carousel>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form - Appears first on mobile */}
            <div className="order-1 lg:order-1">
              <h2 className="text-3xl font-bold text-foreground mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={() => handleBlur('name')}
                    placeholder="John Doe"
                    className={`w-full ${touched.name && errors.name ? 'border-red-500' : ''}`}
                  />
                  {touched.name && errors.name && (
                    <div className="flex items-center gap-1 mt-1 text-red-500 text-sm">
                      <AlertCircle size={14} />
                      <span>{errors.name}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={() => handleBlur('email')}
                    placeholder="user@example.com"
                    className={`w-full ${touched.email && errors.email ? 'border-red-500' : ''}`}
                  />
                  {touched.email && errors.email && (
                    <div className="flex items-center gap-1 mt-1 text-red-500 text-sm">
                      <AlertCircle size={14} />
                      <span>{errors.email}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={() => handleBlur('message')}
                    placeholder="Tell us more about your needs..."
                    className={`w-full ${touched.message && errors.message ? 'border-red-500' : ''}`}
                  />
                  {touched.message && errors.message && (
                    <div className="flex items-center gap-1 mt-1 text-red-500 text-sm">
                      <AlertCircle size={14} />
                      <span>{errors.message}</span>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg shadow-lg shadow-primary/50 hover:shadow-xl shadow-primary/20 transition-all duration-300"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && <Send className="ml-2" size={20} />}
                </Button>
              </form>
            </div>

            {/* Map and Additional Info - Appears second on mobile */}
            <div className="space-y-8 order-2 lg:order-2">
              {/* <Card>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-foreground mb-4">Our Location</h3>
                  <div className="aspect-video rounded-lg mb-4 overflow-hidden border border-border">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3152.9407205085624!2d-122.40621668468154!3d37.79307897975764!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085808c5d2f8d1d%3A0x6b3a5e1e5f9e5e5e!2s650%20California%20St%2C%20San%20Francisco%2C%20CA%2094108!5e0!3m2!1sen!2sus!4v1634567890123!5m2!1sen!2sus"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="ALM Group Office Location"
                    ></iframe>
                  </div>
                  <div className="space-y-3 text-muted-foreground">
                    <p className="flex items-start">
                      <MapPin className="mr-2 flex-shrink-0 mt-1 text-primary" size={20} />
                      {companyInfo.address}
                    </p>
                  </div>
                </CardContent>
              </Card> */}

              <Card className="bg-gradient-to-br from-primary/20 via-purple-600/10 to-background text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-4">Ready to Start?</h3>
                  <p className="text-muted-foreground mb-6">
                    Whether you need trading solutions, business consulting, IT services, or custom software, 
                    our team is ready to help you succeed.
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-300 rounded-full mr-3" />
                      Free initial consultation
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-300 rounded-full mr-3" />
                      Custom solutions for your needs
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-blue-300 rounded-full mr-3" />
                      24/7 support available
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
